import { extname } from 'node:path';
import { MIMEType } from 'node:util';
import {
    APPLICATION_OCTET_STREAM_VALUE, InvalidMimeTypeError,
    isWildcardSubtype,
    isWildcardType
} from '../utils/mime-type-utils.ts';

//excerpt from mime types database
const mimeTypes = `
application/octet-stream    bin dms lrf mar so dist distz pkg bpk dump elc deploy
application/json            json
application/wasm            wasm
application/x-pkcs12        p12 pfx
text/css                    css
text/csv                    csv
text/html                   html htm
text/javascript             js mjs
text/plain                  txt text conf def list log in
`

export class MediaType extends MIMEType {
    static readonly ALL: MediaType = new MediaType('*/*');
    static readonly APPLICATION_OCTET_STREAM: MediaType = new MediaType(APPLICATION_OCTET_STREAM_VALUE);
    static readonly APPLICATION_JSON: MediaType = new MediaType('application/json');
    static readonly APPLICATION_NDJSON: MediaType = new MediaType('application/x-ndjson');

    static readonly MULTIPART_FORM_DATA: MediaType = new MediaType('multipart/form-data');

    static readonly TEXT_PLAIN: MediaType = new MediaType('text/plain');
    static readonly TEXT_HTML: MediaType = new MediaType('text/html');

    static readonly #PARAM_QUALITY_FACTOR = 'q';

    private constructor(input: string) {
        super(input);
        this.#checkToken(this.type);
        this.#checkToken(this.subtype);
        for (const [key, value] of this.params) {
            this.checkParameters(key, value);
        }
    }

    protected checkParameters(key: string, value: string) {
        this.#checkToken(key);
        if (!isQuoted(value)) {
            this.#checkToken(value);
        }
        if (key === MediaType.#PARAM_QUALITY_FACTOR) {
            const unquotedValue = unquote(value);
            const q = parseFloat(unquotedValue);
            if (isNaN(q) || q < 0.0 || q > 1.0) {
                throw new InvalidMediaTypeError(`Invalid quality value: ${value}`);
            }
        }
    }

    #checkToken(token: string) {
        for (let i = 0; i < token.length; i++) {
            const c = token.charCodeAt(i);
            if (c < 0x20 // control characters
                || c > 0x7E // non-ASCII characters
                || c === 0x28 // (
                || c === 0x29 // )
                || c === 0x3C // <
                || c === 0x3E // >
                || c === 0x40 // @
                || c === 0x2C // ,
                || c === 0x3B // ;
                || c === 0x3A // :
                || c === 0x5C // \
                || c === 0x22 // "
                || c === 0x35 // /
                || c === 0x5B // [
                || c === 0x5D // ]
                || c === 0x3F // ?
                || c === 0x3D // =
                || c === 0x7B // {
                || c === 0x7D // }
                || c === 0x20 // space
                || c === 0x09 // tab
            ) {
                throw new InvalidMediaTypeError(`Invalid token: ${token}`);
            }
        }
    }


    get quality(): number {
        const qualityFactor = this.params.get(MediaType.#PARAM_QUALITY_FACTOR);
        return (qualityFactor !== null) ? parseFloat(unquote(qualityFactor)) : 1.0;
    }

    static parseMediaType(input: string): MediaType {
        return new MediaType(input);
    }

    static parseMediaTypesList(mediaTypes: string): MediaType[] {
        if (!mediaTypes?.trim()) {
            return [];
        }
        return mediaTypes.split(',')
            .map(type => type.trim())
            .filter(type => type.length > 0)
            .map(type => MediaType.parseMediaType(type));
    }

    static parseMediaTypes(...mediaTypes: string[]): MediaType[] {
        if (!mediaTypes) {
            return [];
        }
        else if (mediaTypes.length === 1) {
            return MediaType.parseMediaTypesList(mediaTypes[0]);
        }
        else {
            return mediaTypes.flatMap(mediaType => MediaType.parseMediaTypesList(mediaType));
        }
    }

    static asMediaType(mimeType: MIMEType): MediaType {
        if (mimeType instanceof MediaType) {
            return mimeType;
        }
        return new MediaType(mimeType.toString());
    }

    static asMediaTypes(...mimeTypes: MIMEType[]): MediaType[] {
        return mimeTypes.map(mimeType => MediaType.asMediaType(mimeType));
    }
}

export class InvalidMediaTypeError extends Error {
    constructor(message: string) {
        super(message);
    }
}

const fileExtensionToMediaTypes: Map<string, MediaType[]> = (function parseMimeTypes(): Map<string, MediaType[]> {
    const map = new Map<string, MediaType[]>();

    for (const line of mimeTypes.trim().split('\n')) {
        const trimmed = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;

        const parts = trimmed.split(/\s+/);
        if (parts.length < 2) continue;

        const [mimeType, ...extensions] = parts;
        const mediaType = MediaType.parseMediaType(mimeType);

        for (const ext of extensions) {
            const key = '.' + ext.toLowerCase();
            const existing = map.get(key);
            if (existing) {
                existing.push(mediaType);
            }
            else {
                map.set(key, [mediaType]);
            }
        }
    }

    return map;
})();

export function getMediaType(filename?: string): MediaType | undefined {
    return getMediaTypes(filename)[0];
}

export function getMediaTypes(filename?: string): MediaType[] {
    const extension = filename ? extname(filename) : '';
    let mediaTypes: MediaType[] | undefined = undefined;
    if (extension) {
        mediaTypes = fileExtensionToMediaTypes.get(extension.toLowerCase());
    }
    return mediaTypes ?? [];
}

function isQuoted(s: string): boolean {
    return s.length >= 2 && (
        (s.startsWith('"') && s.endsWith('"')) ||
        (s.startsWith("'") && s.endsWith("'")));
}

function unquote(s: string) {
    if (isQuoted(s)) {
        return s.substring(1, s.length - 1);
    }
    return s;
}

function isMoreSpecific(a: MIMEType, b: MIMEType): boolean {
    if (a instanceof MediaType) {
        if (b instanceof MediaType) {
            const aQuality = a.quality;
            const bQuality = b.quality;
            if (aQuality > bQuality) {
                return true;
            }
            else if (aQuality < bQuality) {
                return false;
            }
        }
    }
    const aWildcard = isWildcardType(a);
    const bWildcard = isWildcardType(b);
    if (aWildcard && !bWildcard) { // */* > text/*
        return false;
    }
    else if (!aWildcard && bWildcard) { // text/* < */*
        return true;
    }
    else {
        const aWildcardSubtype = isWildcardSubtype(a);
        const bWildcardSubtype = isWildcardSubtype(b);
        if (aWildcardSubtype && !bWildcardSubtype) { // text/* > text/html
            return false;
        }
        else if (!aWildcardSubtype && bWildcardSubtype) { // text/html < text/*
            return true;
        }
        else if (a.type === b.type && a.subtype === b.subtype) {
            const aParamsSize = Array.from(a.params.keys()).length;
            const bParamsSize = Array.from(b.params.keys()).length;
            return aParamsSize > bParamsSize; // text/html;level=1 > text/html
        }
        else {
            return false; // both are equally specific
        }
    }
}

function isLessSpecific(a: MIMEType, b: MIMEType): boolean {
    return isMoreSpecific(b, a);
}

function bubbleSort<T>(list: T[], swap: (a: T, b: T) => boolean) {
    const n = list.length;
    for (let i = 0; i < n; i++) {
        for (let j = 1; j < n - i; j++) {
            const prev = list[j - 1];
            const curr = list[j];
            if (swap(prev, curr)) {
                list[j] = prev;
                list[j - 1] = curr;
            }
        }
    }
}

export function sortBySpecificity<T extends MIMEType>(mimeTypes: T[]) {
    if (mimeTypes === undefined) {
        throw new Error('mimeTypes must not be undefined');
    }
    if (mimeTypes.length > 100) {
        throw new InvalidMimeTypeError(`too many elements`);
    }
    bubbleSort(mimeTypes, isLessSpecific);

}
